
/**
 * Module dependencies.
 */

import { Button } from './buttons/button';
import { CheckedIcon, LabelText, UncheckedIcon } from './checkbox';
import { Icon } from 'react-components/media';
import { ReactNode, createContext, useCallback, useContext } from 'react';
import { boxShadow } from './styles/box-shadow';
import { color, media } from 'react-components/styles';
import { colors } from './styles/colors';
import { compact, find, isEmpty, isNil, map } from 'lodash';
import { ifProp, theme } from 'styled-tools';
import { textDesktopStyle } from './forms/fields-styled-components';
import { typography } from './typography/typography-styles';
import ReactSelect, {
  CoercedMenuPlacement,
  CommonProps,
  MenuPlacement,
  MenuPosition,
  components as RSComponents,
  StylesConfig
} from 'react-select';

import addIcon from './assets/svg/16/add.svg';
import caretIcon from './assets/svg/24/caret.svg';
import checkedIcon from './assets/svg/forms/checkbox-checked.svg';
import closeIcon from './assets/svg/16/close.svg';
import styled, { ThemeContext, css } from 'styled-components';
import uncheckedIcon from './assets/svg/forms/checkbox.svg';

/**
 * Export `selectClassNamePrefix`.
 */

export const selectClassNamePrefix = 'react-select';

/**
 * `Value` type.
 */

type Value = string | (string | Record<string, unknown>)[] | Record<string, unknown>;

/**
 * Export `ItemOption` type.
 */

export type ItemOption = {
  label: string;
  value: Value;
};

/**
 * Export `MenuAction` type.
 */

export type MenuAction = {
  actionLabel?: string;
  onAction?: () => void;
};

/**
 * `Props` type.
 */

type Props = MenuAction & {
  cacheOptions?: boolean;
  className?: string;
  closeMenuOnSelect?: boolean;
  components?: any;
  data?: any;
  disabled?: boolean;
  hideSelectedOptions?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  isMulti?: boolean;
  isSearchable?: boolean;
  name: string;
  noOptionsMessage?: (values: { inputValue: string }) => ReactNode;
  onChange: (value: Value | null | undefined) => void;
  onClearValue?: () => void;
  onCloseMenuOnScroll?: (event: Event) => boolean;
  options: Array<ItemOption>;
  placeholder?: string;
  value: Value | null | undefined;
};

/**
 * `BackgroundColorProps` type.
 */

type BackgroundColorProps = {
  isFocused: boolean;
  isSelected: boolean;
  theme: any;
};

/**
 * `Option` type.
 */

type Option = {
  isFocused: boolean;
  isSelected: boolean;
};

/**
 * `ReactSelectProps` type.
 */

type ReactSelectProps = CommonProps<ItemOption, boolean, any> & {
  children: ReactNode;
  getPortalPlacement: any;
  innerProps: any;
  innerRef: any;
  maxMenuHeight: number;
  menuPlacement: MenuPlacement;
  menuPosition: MenuPosition;
  menuShouldScrollIntoView: boolean;
  minMenuHeight: number;
};

/**
 * `MenuWithActionProps` type.
 */

type MenuWithActionProps = MenuAction & ReactSelectProps;

/**
 * `CustomOptionProps` type.
 */

type CustomOptionProps = ReactSelectProps & {
  data: any;
  isDisabled: boolean;
  isFocused: boolean;
  isSelected: boolean;
  label: string;
  type: 'option';
  value: string;
};

/**
 * `DropdownIndicatorPropss` type.
 */

type DropdownIndicatorProps = ReactSelectProps & {
  isDisabled: boolean;
  isFocused: boolean;
};

/**
 * Resolve select values.
 */

function resolveSelectValues(
  isMulti: boolean,
  options: Array<ItemOption>,
  value: Value | null | undefined
): ItemOption | Array<ItemOption> | undefined {
  if (isNil(value)) {
    return undefined;
  }

  if (isMulti) {
    return compact(map(value as Array<string | Record<string, unknown>>, selectedValue => {
      const currentValue = find(options, { value: selectedValue });

      if (isNil(currentValue)) {
        return;
      }

      return {
        label: currentValue.label,
        value: currentValue.value
      };
    }));
  }

  return find(options, { value });
}

/**
 * Resolve value.
 */

function resolveValue(data: any, isMulti: boolean): Value | undefined {
  if (isNil(data) || isEmpty(data)) {
    return undefined;
  }

  return isMulti ? map(data, ({ value }) => value) : data.value;
}

/**
 * Get background color.
 */

function getBackgroundColor(backgroundColorProps: BackgroundColorProps): string {
  const { isFocused, isSelected, theme } = backgroundColorProps;

  if (isSelected) {
    return theme.colors.info100;
  }

  if (isFocused) {
    return theme.colors.gray200;
  }

  return theme.colors.white;
}

/**
 * Get menu styles.
 */

function getMenuStyles(theme: any, isMulti: boolean, hasMenuWithAction: boolean): StylesConfig {
  return {
    menu: (styles: any) => ({
      ...styles,
      backgroundColor: theme.colors.white,
      border: 0,
      borderRadius: '6px',
      boxShadow: boxShadow.card,
      display: 'flex',
      flexDirection: 'column',
      margin: '4px 0 4px'
    }),
    menuList: (styles: any) => ({ ...styles, padding: 0 }),
    menuPortal: (styles: any) => ({ ...styles, zIndex: 100 }),
    multiValue: (styles: any) => ({
      ...styles,
      ':not(:first-of-type):before': {
        content: '", "',
        display: 'block',
        width: '6px'
      },
      backgroundColor: 'transparent',
      borderRadius: 'none',
      display: 'flex',
      margin: 0,
      minWidth: 'none'
    }),
    multiValueLabel: (styles: any) => ({
      ...styles,
      borderRadius: 'none',
      color: theme.colors.secondary,
      padding: 0,
      paddingLeft: 0
    }),
    multiValueRemove: (styles: any) => ({
      ...styles,
      display: 'none'
    }),
    option: (styles: any, { isFocused, isSelected }: Option) => ({
      ...styles,
      ...isMulti ? {
        alignItems: 'center',
        backgroundColor: isFocused ? theme.colors.gray200 : theme.colors.white,
        color: theme.colors.nightBlue,
        cursor: 'pointer',
        display: 'grid',
        gridColumnGap: '8px',
        gridTemplateAreas: '\'checkbox label\'',
        gridTemplateColumns: '20px 1fr',
        padding: '12px 16px 11px'
      } : {
        backgroundColor: getBackgroundColor({ isFocused, isSelected, theme }),
        color: isFocused || isSelected ? theme.colors.primary : theme.colors.nightBlue,
        cursor: isSelected ? 'default' : 'pointer',
        padding: '12px 16px 11px',
        pointerEvents: isSelected ? 'none' : 'auto'
      },
      ':first-of-type': {
        borderRadius: '4px 4px 0 0'
      },
      ':hover': {
        '*': {
          color: theme.colors.blue
        },
        backgroundColor: theme.colors.gray200
      },
      ...hasMenuWithAction ? {
        borderBottom: `1px solid ${theme.colors.gray300}`
      } : {
        ':last-of-type': {
          paddingBottom: '12px'
        },
        ':not(:last-of-type)': {
          borderBottom: `1px solid ${theme.colors.gray300}`
        }
      },
      fontFamily: typography.fontFamilies.inter,
      fontSize: '16px',
      fontWeight: '400',
      letterSpacing: '-0.5px',
      lineHeight: '22px'
    })
  };
}

/**
 * Select context.
 */

const SelectContext = createContext<MenuAction>({ onAction: () => {} });

/**
 * `StyledReactSelect` styled component.
 */

const StyledReactSelect = styled(ReactSelect)<{ isDisabled: boolean; isMulti: boolean }>`
  .${selectClassNamePrefix} {
    &__control {
      background: none;
      border: 0;
      border-radius: 0;
      box-shadow: none;
      color: ${color('secondary')};
      display: block;
      padding: 14px 68px 4px 10px;
      width: 100%;

      ${media.min('ms')`
        padding-left: 14px;
        padding-top: 16px;
      `}
    }

    &__value-container {
      padding: 0;

      ${ifProp('isMulti', css`
        flex-wrap: nowrap;
      `)}
    }

    &__single-value {
      ${textDesktopStyle}

      color: ${color('secondary')};
      margin: 0;
      transition: color ${theme('animations.defaultTransition')};

      ${ifProp('isDisabled', css`
        color: ${color('gray400')};
      `)}
    }
  }
`;

/**
 * `IndicatorsContainer` styled component.
 */

const IndicatorsContainer = styled.div`
  align-items: center;
  display: flex;
  height: 24px;
  justify-content: flex-end;
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
`;

/**
 * `CaretIcon` styled component.
 */

const CaretIcon = styled(Icon).attrs({ 'aria-hidden': true, icon: caretIcon })<{
  isDisabled: boolean;
  isFocused: boolean;
}>`
  color: ${colors.gray500};
  max-width: initial;
  /* stylelint-disable plugin/no-low-performance-animation-properties */
  transition: 0.25s ease;
  transition-property: color, transform;
  /* stylelint-enable plugin/no-low-performance-animation-properties */
  width: 24px;

  ${ifProp('isFocused', css`
    transform: scaleY(-1);
  `)}

  ${ifProp('isDisabled', css`
    color: ${color('gray400')};
  `)}
`;

/**
 * `ClearIcon` styled component.
 */

const ClearIcon = styled(Icon).attrs({ 'aria-hidden': true, icon: closeIcon })`
  color: ${colors.gray500};
  cursor: pointer;
  margin-right: 6px;
  max-width: initial;
  transition: color 0.3s ease-out;
  width: 16px;

  &:hover {
    color: ${colors.gray600};
  }
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  color: ${color('gray600')};
  flex: 1;
  margin: 8px;
`;

/**
 * `StyledCheckedIcon` styled component.
 */

const StyledCheckedIcon = styled(CheckedIcon).attrs({ icon: checkedIcon })<{ isSelected: boolean }>`
  grid-area: checkbox;

  ${ifProp('isSelected', css`
    opacity: 1;
  `)}
`;

/**
 * `StyledUncheckedIcon` styled component.
 */

const StyledUncheckedIcon = styled(UncheckedIcon).attrs({ icon: uncheckedIcon })<{ isSelected: boolean }>`
  grid-area: checkbox;

  ${ifProp('isSelected', css`
    opacity: 0;
  `)}
`;

/**
 * `DropdownIndicator` component.
 */

function DropdownIndicator(props: DropdownIndicatorProps): JSX.Element {
  const { innerProps: { ref, ...restInnerProps } } = props;

  return (
    <CaretIcon
      {...restInnerProps}
      isDisabled={props?.isDisabled}
      isFocused={props?.isFocused}
      ref={ref}
    />
  );
}

/**
 * `ClearIndicator` component.
 */

function ClearIndicator(props: ReactSelectProps): JSX.Element {
  const { innerProps: { ref, ...restInnerProps } } = props;

  return (
    <ClearIcon
      {...restInnerProps}
      ref={ref}
    />
  );
}

/**
 * `MenuWithAction` component.
 */

function MenuWithAction(props: MenuWithActionProps): JSX.Element {
  const { children, menuPlacement, ...rest } = props;
  const { actionLabel, onAction } = useContext(SelectContext);

  return (
    <RSComponents.Menu<ItemOption, boolean, any>
      {...rest}
      isLoading={false}
      menuPlacement={menuPlacement}
      placement={menuPlacement === 'auto' ? 'bottom' : menuPlacement as CoercedMenuPlacement}
    >
      <>
        {children}

        {actionLabel && onAction && (
          <StyledButton
            colorTheme={'gray'}
            icon={addIcon}
            iconSize={'small'}
            onClick={onAction}
            size={'medium'}
          >
            {actionLabel}
          </StyledButton>
        )}
      </>
    </RSComponents.Menu>
  );
}

/**
 * `CustomOptionWithCheckbox` component.
 */

function CustomOptionWithCheckbox(props: CustomOptionProps): JSX.Element {
  const { isSelected, label } = props;

  return (
    <RSComponents.Option {...props}>
      <LabelText>
        {label}
      </LabelText>

      <StyledCheckedIcon isSelected={isSelected} />

      <StyledUncheckedIcon isSelected={isSelected} />
    </RSComponents.Option>
  );
}

/**
 * Export `BaseSelect` component.
 */

export function BaseSelect(props: Props): JSX.Element {
  const {
    actionLabel,
    closeMenuOnSelect,
    components,
    disabled = false,
    hideSelectedOptions,
    isClearable,
    isMulti = false,
    name,
    onAction,
    onChange,
    onClearValue,
    onCloseMenuOnScroll,
    options,
    placeholder = '',
    value,
    ...rest
  } = props;

  const selectValues = resolveSelectValues(isMulti, options, value);
  const theme = useContext(ThemeContext);
  const hasMenuWithAction = !!actionLabel && !!onAction;
  const handleChange = useCallback((data: any, { action }: any) => {
    if (onClearValue && action === 'clear') {
      onClearValue();
    }

    if (onChange) {
      return onChange(resolveValue(data, isMulti));
    }
  }, [isMulti, onChange, onClearValue]);

  return (
    <SelectContext.Provider value={{ actionLabel, onAction }}>
      <StyledReactSelect
        aria-labelledby={`${name}_label`}
        classNamePrefix={selectClassNamePrefix}
        closeMenuOnScroll={onCloseMenuOnScroll}
        closeMenuOnSelect={closeMenuOnSelect}
        components={{
          ClearIndicator,
          DropdownIndicator,
          IndicatorSeparator: '',
          IndicatorsContainer,
          LoadingIndicator: '',
          Menu: MenuWithAction,
          ...isMulti ? { Option: CustomOptionWithCheckbox } : {},
          ...components
        }}
        hideSelectedOptions={hideSelectedOptions}
        inputId={name}
        isClearable={isClearable}
        isDisabled={disabled}
        isMulti={isMulti}
        menuPortalTarget={document.getElementById('select-menu-root')}
        name={name}
        onChange={handleChange}
        options={options}
        placeholder={placeholder}
        styles={getMenuStyles(theme, isMulti, hasMenuWithAction)}
        value={selectValues}
        {...rest}
      />
    </SelectContext.Provider>
  );
}
